// Date/time formatters used across the widget.
//
// Caveat — middleware TZ behavior:
// The middleware's stub adapter currently treats store-local HH:mm strings as
// UTC (per the Phase 1A production gaps memo, item 4). So a slot at
// "2026-05-27T10:00:00.000Z" was MEANT to be 10:00 AM in the store's local
// timezone, not 10:00 UTC.
//
// Until the middleware adopts proper TZ math, the widget displays the HH:MM
// components from the ISO string AS-IS without any timezone conversion. This
// gives the correct user-facing time in every store regardless of browser TZ.
//
// When the middleware fix lands, switch these helpers to:
//   new Intl.DateTimeFormat('en-US', { timeZone: storeTimezone, ... }).format(date)

/**
 * Add minutes to a fake-Z ISO string, staying in fake-Z.
 *
 * The components are parsed out of the string and the arithmetic is done
 * entirely in UTC space (Date.UTC in, toISOString out) — the browser's local
 * timezone never participates, so the store wall-clock behind the fake Z is
 * preserved exactly. Used by the reschedule flow to construct a slot end from
 * the looked-up start + the fitting's duration.
 *
 * Returns the input unchanged when it doesn't look like an ISO date-time —
 * callers treat that as "no slot" rather than producing a garbage end.
 */
export function addMinutesToFakeZ(iso: string, minutes: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/.exec(iso);
  if (!m) return iso;
  const ms =
    Date.UTC(
      parseInt(m[1], 10),
      parseInt(m[2], 10) - 1,
      parseInt(m[3], 10),
      parseInt(m[4], 10),
      parseInt(m[5], 10),
      m[6] ? parseInt(m[6], 10) : 0,
    ) +
    minutes * 60_000;
  return new Date(ms).toISOString();
}

export function formatSlotTime(iso: string): string {
  // Expect "YYYY-MM-DDTHH:mm:ss(.SSS)?Z?" — pull HH:mm out directly
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return iso;
  const hour24 = parseInt(m[1], 10);
  const minute = m[2];
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  const hour = hour24 % 12 || 12;
  return `${hour}:${minute} ${ampm}`;
}

function ordinalSuffix(n: number): string {
  if (n % 10 === 1 && n !== 11) return 'st';
  if (n % 10 === 2 && n !== 12) return 'nd';
  if (n % 10 === 3 && n !== 13) return 'rd';
  return 'th';
}

export function formatPillDate(date: string, locale: string = 'en-US'): string {
  // en-US → "Wed May 20th"; en-GB / en-AU → "Wed, 20 May"; en-CA → "Wed, May 20"
  const d = new Date(date + 'T00:00:00');
  if (locale === 'en-US') {
    const day = d.toLocaleDateString('en-US', { weekday: 'short' });
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const dayNum = d.getDate();
    return `${day} ${month} ${dayNum}${ordinalSuffix(dayNum)}`;
  }
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(d);
}

// User-facing date format used on every "the booking is on …" surface:
// confirm appointment, congrats, mobile selection summary.
//   en-US → "December 31st, 2026"
//   en-GB → "31 December 2026"
//   en-AU → "31 December 2026"
//   en-CA → "December 31, 2026"
// US path keeps the ordinal + comma the widget shipped with; every other
// locale falls through to Intl.DateTimeFormat's `dateStyle: 'long'` so
// each market renders the format its users actually expect.
export function formatLongDate(date: string, locale: string = 'en-US'): string {
  const d = new Date(date + 'T00:00:00');
  if (locale === 'en-US') {
    const month = d.toLocaleDateString('en-US', { month: 'long' });
    const dayNum = d.getDate();
    const year = d.getFullYear();
    return `${month} ${dayNum}${ordinalSuffix(dayNum)}, ${year}`;
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(d);
}
